import isObject from 'lodash/isObject';
import sortBy from 'lodash/sortBy';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IMetrics, metrics } from '../data/providers/metricsProvider';
import {
  IMetricSettings,
  metricSettings,
} from '../data/providers/settings/metricSettingsProvider';
import {
  IMetric,
  IMetricMap,
  ITest,
  ITestRepetition,
  ITestRepetitionIntermediate,
} from '../data/types';
import { convertTestToImperial } from './conversions';

/**
 * @description merges the metric metadata with each test
 * @param data ITest[]
 * @returns ITest[]
 */
export const mergeMetrics = (
  data: ITest[],
  includeInactiveMetrics?: boolean
): Observable<ITest[]> => {
  const knownTests = data.filter((test: ITest): boolean =>
    Boolean(test.testType)
  );

  return combineLatest([metrics, metricSettings]).pipe(
    map((result: [IMetrics, IMetricSettings]): ITest[] => {
      const [allMetrics, settings] = result;
      return knownTests.map((test: ITest): ITest => {
        const meta = allMetrics[test.testType.id];
        const testWithMetrics = Object.keys(meta).reduce(
          (acc: ITest, curr: string): ITest => {
            const metricMeta = meta[curr];
            const includeMetric = includeInactiveMetrics
              ? true
              : settings?.metrics?.[test.testType.id]?.[curr] !== false;
            if (metricMeta && includeMetric) {
              acc[curr] = {
                ...metricMeta,
                value: isFinite(test[curr]) ? test[curr] : null,
                key: curr,
              };
            } else {
              acc[curr] = test[curr];
            }
            return acc;
          },
          { ...test }
        );
        if (!settings.imperial) return testWithMetrics;
        return convertTestToImperial(testWithMetrics);
      });
    })
  );
};

export const mergeRepetitionMetrics = (
  data: ITestRepetitionIntermediate[],
  includeInactiveMetrics?: boolean
): Observable<ITestRepetition[]> => {
  const knownTests = data.filter((test: ITestRepetitionIntermediate): boolean =>
    Boolean(test.testType)
  );

  return combineLatest([metrics, metricSettings]).pipe(
    map((result: [IMetrics, IMetricSettings]): ITestRepetition[] => {
      const [allMetrics, settings] = result;
      return knownTests.map(
        (test: ITestRepetitionIntermediate): ITestRepetition => {
          const mMetrics = allMetrics[test.testType.id];
          const testWithMetrics = { ...test } as ITestRepetition;

          for (const metricId of Object.keys(mMetrics)) {
            const metricMeta = mMetrics[metricId];
            const includeMetric = includeInactiveMetrics
              ? true
              : settings?.metrics?.[test.testType.id]?.[metricId] !== false;
            if (metricMeta && includeMetric) {
              testWithMetrics.metrics[metricId] = {
                ...metricMeta,
                value: isFinite(test.metrics[metricId])
                  ? test.metrics[metricId]
                  : null,
                key: metricId,
              };
            }
          }
          if (!settings.imperial) return testWithMetrics;
          return convertTestToImperial(testWithMetrics);
        }
      );
    })
  );
};

const testMetaKeys = [
  'id',
  'active',
  'athleteId',
  'athlete',
  'appVersion',
  'controllerVersion',
  'generatedAt',
  'groups',
  'sessionId',
  'testType',
  'queryHash',
  'timestamp',
  'tags',
  'teams',
];
/**
 * @description filter out the active metrics
 * @param data
 * @param active
 * @returns
 */
export const filterActiveMetrics = (test: ITest, active: IMetrics): ITest => {
  /// include the metadata always
  const metadata = testMetaKeys.reduce(
    (acc: ITest, curr: string): ITest => {
      acc[curr] = test[curr];
      return acc;
    },
    { id: null }
  );
  /// get the active metrics for the test type
  const metricKeys = active[test.testType.id];
  const mMetrics = Object.keys(metricKeys).reduce(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      acc: { [key: string]: any },
      metricKey: string
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): { [key: string]: any } => {
      acc[metricKey] = test[metricKey];
      return acc;
    },
    {}
  );
  return {
    ...metadata,
    ...mMetrics,
  };
};

/**
 * @description generates list of active metrics
 * @param test
 * @returns
 */
export const toActiveMetricList = (
  test: ITest,
  activeMetricMap: IMetricMap
): IMetric[] => {
  const activeKeys = Object.keys(activeMetricMap ?? {});
  return sortBy(
    Object.keys(test)
      .filter((key: string): boolean => !testMetaKeys.includes(key))
      .filter((key: string): boolean => activeKeys.includes(key))
      // eslint-disable-next-line @typescript-eslint/tslint/config
      .map((key: string) => test[key])
      .filter((value: IMetric | string | number): boolean => isObject(value)),
    (mMetric: IMetric): number => mMetric.order
  );
};

/**
 * @description generates list of metrics
 * @param test
 * @returns
 */
export const toMetricList = (test: ITest): IMetric[] => {
  return sortBy(
    Object.keys(test)
      .filter((key: string): boolean => !testMetaKeys.includes(key))
      // eslint-disable-next-line @typescript-eslint/tslint/config
      .map((key: string) => test[key])
      .filter((value: IMetric | string | number): boolean => isObject(value)),
    (mMetric: IMetric): number => mMetric.order
  );
};
